(function (global) {
  'use strict';
  const keys = ['size', 'brand', 'type', 'color'];
  const normalize = value => String(value || '').toLocaleLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
  function facetGroup(key, value) {
    const text = String(value || '').trim();
    if (key === 'size') {
      const range = text.match(/^(\d+)\s*[/／]\s*(\d+)$/);
      if (range && Math.abs(Number(range[1]) - Number(range[2])) === 1) return String(Number(range[1]) % 2 === 0 ? Number(range[1]) : Number(range[2]));
      // EU decimal/mixed-fraction sizes share a whole-size filter; retain ranges.
      const match = text.match(/^(\d+)(?:(?:[.,]\d+)|(?:\s+\d+\s*[/⁄]\s*\d+)|[½⅓⅔¼¾])?$/);
      return match ? String(Number(match[1])) : text;
    }
    if (key === 'color') {
      const base = normalize(text.split(/\s*[/／|+,;&]\s*/)[0]).replace(/[-_]+/g, ' ').trim();
      if (!base) return '';
      if (/^(multi|multo|muilt|colorful|camouflage)/.test(base)) return 'Multicolour';
      // Keep the first colour of a combination; collapse spelling variants and shades.
      if (/^(off\s*white|ofwhite)/.test(base)) return 'White';
      const aliases = {
        black: 'Black', blk: 'Black', blak: 'Black', blcak: 'Black', blavk: 'Black', ebony: 'Black',
        white: 'White', wht: 'White', whit: 'White', whte: 'White', ivory: 'White', cream: 'White', lactic: 'White', ecru: 'White',
        grey: 'Grey', gray: 'Grey', gry: 'Grey', gary: 'Grey', gret: 'Grey', charcoal: 'Grey', ash: 'Grey', slate: 'Grey', smoked: 'Grey', gunmeta: 'Grey', gunmetal: 'Grey',
        blue: 'Blue', navy: 'Blue', aqua: 'Blue', cyan: 'Blue', cayn: 'Blue', turquoise: 'Blue', turqueise: 'Blue', teal: 'Blue', poseidon: 'Blue', royal: 'Blue', denim: 'Blue',
        green: 'Green', olive: 'Green', sage: 'Green', lime: 'Green', lim: 'Green', seafoam: 'Green',
        red: 'Red', burgundy: 'Red', burgandy: 'Red', burg: 'Red', wine: 'Red', maroon: 'Red', scarlet: 'Red',
        pink: 'Pink', rose: 'Pink', blush: 'Pink', fuchsia: 'Pink', fuschia: 'Pink', magenta: 'Pink', salmon: 'Pink',
        purple: 'Purple', purpel: 'Purple', purble: 'Purple', lavender: 'Purple', lavander: 'Purple', lilac: 'Purple', mauve: 'Purple', violet: 'Purple',
        yellow: 'Yellow', yeelow: 'Yellow', lemon: 'Yellow', limon: 'Yellow', honey: 'Yellow', mustard: 'Yellow',
        orange: 'Orange', ogrange: 'Orange', coral: 'Orange', rust: 'Orange',
        brown: 'Brown', coffee: 'Brown', coffe: 'Brown', chocolate: 'Brown', choclat: 'Brown', cognac: 'Brown', camel: 'Brown', tan: 'Brown', clay: 'Brown',
        beige: 'Beige', biege: 'Beige', begie: 'Beige', bige: 'Beige', sand: 'Beige', natural: 'Beige', nat: 'Beige', taupe: 'Beige', khaki: 'Beige', nude: 'Beige',
        silver: 'Silver', gold: 'Gold', golden: 'Gold'
      };
      for (const word of base.split(/\s+/)) if (aliases[word]) return aliases[word];
      if (/^(mint|mnt|sae foam|sea foam)$/.test(base)) return 'Green';
      return 'Other';
    }
    return text;
  }
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
  const prepared = new WeakMap();
  let lastFilterSignature, lastCompiled;
  function prepare(product) {
    if (!prepared.has(product)) prepared.set(product, {
      text: normalize([product.title, product.brand, product.type, ...product.variants.map(v => `${v.size} ${v.color}`)].join(' ')),
      variants: product.variants.map(variant => ({ variant, size: facetGroup('size', variant.size), color: facetGroup('color', variant.color) }))
    });
    return prepared.get(product);
  }
  function compile(filters) {
    const signature = JSON.stringify([filters.size, filters.color, filters.q]);
    if (signature !== lastFilterSignature) {
      lastFilterSignature = signature;
      lastCompiled = { size: new Set((filters.size || []).map(v => facetGroup('size', v))), color: new Set((filters.color || []).map(v => facetGroup('color', v))), terms: normalize(filters.q).trim().split(/\s+/).filter(Boolean) };
    }
    return lastCompiled;
  }
  function variantMatches(variant, selected, exclude) {
    return (exclude === 'size' || !selected.size.size || selected.size.has(variant.size)) && (exclude === 'color' || !selected.color.size || selected.color.has(variant.color));
  }
  function matchingVariants(product, filters, exclude, selected = compile(filters)) {
    return prepare(product).variants.filter(v => variantMatches(v, selected, exclude)).map(v => v.variant);
  }
  function matches(product, filters, exclude, selected = compile(filters)) {
    if (exclude !== 'brand' && filters.brand?.length && !filters.brand.includes(product.brand)) return false;
    if (exclude !== 'type' && filters.type?.length && !filters.type.includes(product.type)) return false;
    const record = prepare(product);
    return selected.terms.every(term => record.text.includes(term)) && record.variants.some(v => variantMatches(v, selected, exclude));
  }
  function facetValues(products, filters, key, selected = compile(filters)) {
    const counts = new Map();
    for (const product of products) {
      if (!matches(product, filters, key, selected)) continue;
      const values = ['size', 'color'].includes(key) ? prepare(product).variants.filter(v => variantMatches(v, selected, key)).map(v => v[key]) : [product[key]];
      for (const value of new Set(values.filter(Boolean))) counts.set(value, (counts.get(value) || 0) + 1);
    }
    for (const value of filters[key] || []) { const grouped = facetGroup(key, value); if (!counts.has(grouped)) counts.set(grouped, 0); }
    return [...counts].sort((a, b) => collator.compare(a[0], b[0]));
  }
  if (typeof module !== 'undefined') module.exports = { matches, matchingVariants, facetValues, facetGroup };
  if (!global.document) return;
  const make = (tag, className, text) => { const element = document.createElement(tag); if (className) element.className = className; if (text !== undefined) element.textContent = text; return element; };
  async function mount(root) {
    if (root.dataset.mounted) return;
    root.dataset.mounted = 'true';
    const labels = JSON.parse(root.querySelector('[data-translations]').textContent);
    const filters = { size: [], brand: [], type: [], color: [], q: '', sort: 'stock' };
    const search = root.querySelector('[data-search]');
    const sort = root.querySelector('[data-sort]');
    const money = new Intl.NumberFormat(root.dataset.locale === 'ar' ? 'ar-EG' : 'en-EG', { style: 'currency', currency: 'EGP', maximumFractionDigits: 2 });
    const grid = root.querySelector('[data-grid]');
    const more = root.querySelector('[data-more]');
    const facetNodes = new Map();
    let products = [], rows = [], pageSize = 24, debounce, selection, renderGeneration = 0;
    let renderedUrl = '';
    const yieldToBrowser = () => new Promise(resolve => setTimeout(resolve, 0));
    const currentUrl = () => location.pathname + location.search;
    function readUrl() {
      const params = new URLSearchParams(location.search);
      for (const key of keys) filters[key] = [...new Set(params.getAll(key).map(value => facetGroup(key, value)))];
      filters.q = params.get('q') || ''; filters.sort = params.get('sort') || 'stock';
      search.value = filters.q; sort.value = filters.sort; pageSize = 24;
    }
    function writeUrl() {
      const params = new URLSearchParams();
      for (const key of keys) for (const value of filters[key]) params.append(key, value);
      if (filters.q) params.set('q', filters.q);
      if (filters.sort !== 'stock') params.set('sort', filters.sort);
      history.replaceState({ ...history.state, dbCollection: null }, '', location.pathname + (params.size ? '?' + params.toString() : ''));
    }
    function toggle(key, value) {
      filters[key] = filters[key].includes(value) ? filters[key].filter(v => v !== value) : [...filters[key], value];
      pageSize = 24; writeUrl(); render();
    }
    function renderFacets() {
      const focused = document.activeElement;
      for (const key of keys) {
        if (!facetNodes.has(key)) {
          const target = root.querySelector(`[data-facet="${key}"]`);
          const details = make('details', 'db-original-facet'); details.open = true;
          details.append(make('summary', '', target.dataset.label));
          const list = make('div', key === 'size' ? 'db-original-size-values' : 'db-original-values');
          details.append(list); target.replaceChildren(details);
          facetNodes.set(key, { list, values: new Map() });
        }
        const facet = facetNodes.get(key);
        const counts = new Map(facetValues(products, filters, key, selection));
        for (const value of counts.keys()) {
          if (facet.values.has(value)) continue;
          const label = make('label', 'db-original-value');
          const input = make('input'); input.type = 'checkbox'; input.value = value;
          input.addEventListener('change', () => toggle(key, value));
          const count = make('span', 'db-original-facet-count');
          label.append(input, make('span', 'db-original-value-label', value), count);
          facet.values.set(value, { label, input, count });
        }
        for (const [value, node] of facet.values) {
          const count = counts.get(value) || 0;
          node.input.checked = filters[key].includes(value);
          node.input.disabled = count === 0 && !node.input.checked;
          node.label.hidden = count === 0 && !node.input.checked;
          if (node.count.textContent !== String(count)) node.count.textContent = String(count);
        }
        if (facet.list.children.length !== facet.values.size) {
          facet.list.replaceChildren(...[...facet.values].sort((a, b) => collator.compare(a[0], b[0])).map(([, node]) => node.label));
        }
      }
      if (focused?.isConnected && focused.matches('input') && focused.closest('[data-facet]') && document.activeElement !== focused) focused.focus({ preventScroll: true });
    }
    function card(product) {
      const variants = matchingVariants(product, filters, undefined, selection);
      const selected = variants.reduce((best, v) => Number(v.price) < Number(best.price) ? v : best, variants[0]);
      const item = make('li', 'db-original-card');
      const link = make('a', 'db-original-photo');
      link.href = `${root.dataset.root.replace(/\/$/, '')}/products/${encodeURIComponent(product.handle)}?variant=${selected.id}`;
      if (product.images[0]) {
        const picture = make('img'); picture.src = product.images[0] + (product.images[0].includes('?') ? '&' : '?') + 'width=640';
        const imageBase = product.images[0] + (product.images[0].includes('?') ? '&' : '?') + 'width=';
        picture.srcset = [240, 360, 480, 640].map(width => `${imageBase}${width} ${width}w`).join(',');
        picture.sizes = '(min-width: 1400px) 260px, (min-width: 990px) 28vw, (min-width: 750px) 36vw, 46vw';
        picture.decoding = 'async';
        picture.width = 640; picture.height = 640; picture.loading = 'lazy'; picture.alt = product.title; link.append(picture);
      }
      const badges = make('db-card-badges');
      badges.dataset.brand = product.brand;
      badges.dataset.sale = String(Number(selected.compare) > Number(selected.price));
      badges.dataset.saleLabel = labels.sale;
      link.append(badges);
      item.append(link, make('p', 'db-original-card-brand', product.brand));
      const title = make('h2'); const titleLink = make('a', '', product.title); titleLink.href = link.href; title.append(titleLink); item.append(title);
      const price = make('p', 'db-original-card-price'); price.append(make('strong', '', money.format(selected.price)));
      if (Number(selected.compare) > Number(selected.price)) price.append(make('s', '', money.format(selected.compare)));
      item.append(price);
      const sizes = [...new Set(variants.map(v => v.size).filter(Boolean))].sort(collator.compare);
      if (sizes.length) item.append(make('p', 'db-original-card-sizes', `${labels.sizes}: ${sizes.join(' · ')}`));
      return item;
    }
    async function render() {
      const generation = ++renderGeneration;
      renderedUrl = currentUrl();
      selection = compile(filters);
      renderFacets();
      rows = products.filter(product => matches(product, filters, undefined, selection));
      if (filters.sort === 'title') rows.sort((a, b) => collator.compare(a.title, b.title));
      if (filters.sort === 'price-low' || filters.sort === 'price-high') {
        const prices = new Map(rows.map(p => [p, Math.min(...matchingVariants(p, filters, undefined, selection).map(v => Number(v.price)))]));
        const direction = filters.sort === 'price-low' ? 1 : -1;
        rows.sort((a, b) => direction * (prices.get(a) - prices.get(b)));
      }
      // Returning from a product can restore hundreds of cards. Give Chrome
      // time to handle input between batches instead of building them in one task.
      const visibleRows = rows.slice(0, pageSize);
      grid.replaceChildren(...visibleRows.slice(0, 24).map(card));
      root.querySelector('[data-count]').textContent = `${rows.length.toLocaleString()} ${labels.products}`;
      root.querySelector('[data-empty]').hidden = rows.length > 0;
      more.hidden = rows.length <= pageSize;
      more.disabled = visibleRows.length > 24;
      const chips = root.querySelector('[data-chips]'); chips.replaceChildren();
      for (const key of keys) for (const value of filters[key]) {
        const button = make('button', 'db-original-chip', `${value} ×`); button.type = 'button'; button.setAttribute('aria-label', `${labels.remove} ${value}`);
        button.addEventListener('click', () => toggle(key, value)); chips.append(button);
      }
      for (let offset = 24; offset < visibleRows.length; offset += 24) {
        await yieldToBrowser();
        if (generation !== renderGeneration || !root.isConnected) return;
        grid.append(...visibleRows.slice(offset, offset + 24).map(card));
      }
      more.disabled = false;
    }
    function rememberPosition() {
      history.replaceState({ ...history.state, dbCollection: { url: location.pathname + location.search, pageSize, scrollY: scrollY, at: Date.now() } }, '');
    }
    const saved = history.state?.dbCollection;
    readUrl();
    const restore = saved && saved.url === location.pathname + location.search && Date.now() - saved.at < 600000;
    if (restore) pageSize = Math.max(24, Number(saved.pageSize) || 24);
    grid.addEventListener('click', event => { if (event.target.closest('a')) rememberPosition(); });
    global.addEventListener('pagehide', rememberPosition);
    search.addEventListener('input', () => { clearTimeout(debounce); debounce = setTimeout(() => { filters.q = search.value.trim(); pageSize = 24; writeUrl(); render(); }, 160); });
    sort.addEventListener('change', () => { filters.sort = sort.value; pageSize = 24; writeUrl(); render(); });
    root.querySelector('[data-clear]').addEventListener('click', () => { for (const key of keys) filters[key] = []; filters.q = ''; search.value = ''; pageSize = 24; writeUrl(); render(); });
    more.addEventListener('click', () => {
      const next = rows.slice(pageSize, pageSize + 24);
      grid.append(...next.map(card)); pageSize += 24;
      more.hidden = rows.length <= pageSize;
      rememberPosition();
    });
    root.querySelector('[data-toggle-filters]').addEventListener('click', event => { const open = root.classList.toggle('db-original-filters-open'); event.currentTarget.setAttribute('aria-expanded', String(open)); });
    global.addEventListener('popstate', () => {
      // Chrome restores the existing DOM from its Back/Forward cache. Rebuilding
      // that same listing discards the saved page size and fights scroll restore.
      if (currentUrl() === renderedUrl) return;
      readUrl(); render();
    });
    try {
      const response = await fetch(root.dataset.indexUrl, { signal: AbortSignal.timeout(20000) });
      if (!response.ok) throw new Error('Index unavailable');
      const data = await response.json(); products = data.products.filter(p => p.variants?.length);
      const audience = root.dataset.audience;
      const range = { men: [40,50], women: [36,40], kids: [1,35] }[audience];
      if (range) products = products.map(p => ({ ...p, variants: p.variants.filter(v => { const size = Number(facetGroup('size', v.size)); return size >= range[0] && size <= range[1]; }) })).filter(p => p.variants.length);
      for (let offset = 0; offset < products.length; offset += 100) {
        products.slice(offset, offset + 100).forEach(prepare);
        await yieldToBrowser();
        if (!root.isConnected) return;
      }
      pageSize = Math.min(pageSize, Math.max(24, products.length));
      await render();
      root.dataset.ready = 'true';
      if (restore && currentUrl() === saved.url) requestAnimationFrame(() => global.scrollTo(0, saved.scrollY));
    } catch (_) {
      root.querySelector('[data-count]').textContent = labels.error;
      const retry = make('button', 'button', labels.loading); retry.type = 'button'; retry.addEventListener('click', () => location.reload()); root.querySelector('[data-grid]').replaceChildren(retry);
    }
  }
  document.querySelectorAll('[data-original-collection]').forEach(mount);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-original-collection]').forEach(mount));
})(typeof window !== 'undefined' ? window : globalThis);
